import { createContext, useContext } from 'react';
import { jsx } from 'react/jsx-runtime';

// src/event-bus.ts
var MAX_HISTORY = 100;
var StudioEventBus = class {
  subscriptions = /* @__PURE__ */ new Map();
  history = [];
  devToolsObserver;
  /**
   * Subscribe to an event.
   * Returns an unsubscribe function.
   */
  subscribe(event, handler, pluginId, pageId) {
    const sub = {
      event,
      handler,
      pluginId,
      pageId
    };
    if (!this.subscriptions.has(event)) {
      this.subscriptions.set(event, /* @__PURE__ */ new Set());
    }
    this.subscriptions.get(event).add(sub);
    return () => {
      this.subscriptions.get(event)?.delete(sub);
    };
  }
  /**
   * Publish an event to all subscribers.
   */
  publish(event, payload, sourcePluginId, sourcePageId) {
    const meta = {
      sourcePluginId,
      sourcePageId,
      timestamp: Date.now()
    };
    const record = { event, payload, meta };
    this.history.push(record);
    if (this.history.length > MAX_HISTORY) {
      this.history.shift();
    }
    this.devToolsObserver?.(record);
    const subs = this.subscriptions.get(event);
    if (!subs) {
      return;
    }
    for (const sub of subs) {
      try {
        sub.handler(payload, meta);
      } catch (err) {
        console.error(
          `[StudioEventBus] Handler error for "${event}" in ${sub.pluginId}:${sub.pageId}:`,
          err
        );
      }
    }
  }
  /**
   * Unsubscribe all handlers for a specific page instance.
   * Called automatically on page unmount.
   */
  unsubscribeAll(pluginId, pageId) {
    for (const [, subs] of this.subscriptions) {
      for (const sub of subs) {
        if (sub.pluginId === pluginId && sub.pageId === pageId) {
          subs.delete(sub);
        }
      }
    }
  }
  /**
   * Get event history for devtools.
   */
  getHistory() {
    return this.history;
  }
  /**
   * Register a devtools observer that is called on every published event.
   * Only one observer at a time — intended for @kb-labs/studio-devtools only.
   */
  setDevToolsObserver(cb) {
    this.devToolsObserver = cb;
  }
  clearDevToolsObserver() {
    this.devToolsObserver = void 0;
  }
  /**
   * Clear all subscriptions and history. For testing.
   */
  reset() {
    this.subscriptions.clear();
    this.history = [];
  }
};
var eventBus = new StudioEventBus();
var EventBusContext = createContext(eventBus);
function EventBusProvider({ children, bus }) {
  return /* @__PURE__ */ jsx(EventBusContext.Provider, { value: bus ?? eventBus, children });
}
function useEventBusContext() {
  return useContext(EventBusContext);
}

export { EventBusProvider, StudioEventBus, eventBus, useEventBusContext };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map